import { useState } from 'react';
import { Badge, Modal, SimpleTable } from '@insio/ui-toolkit';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { CardActions, CardButton, ResultCard, ResultCardHeader } from '../ResultCard';
import { useValidationPathLabel } from './use-validation-path-label';
import type { IssueCollectionPartData, IssueRowData } from './entity-part.types';

interface Props {
  part: IssueCollectionPartData;
}

/**
 * One card standing in for every validation issue of an invoice. A long list of individual
 * cards buries the answer, so the detail moves behind an expandable table.
 */
export function IssueCollectionPart({ part }: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const t = useFeatureTranslation('aiChat');
  const labelForPath = useValidationPathLabel();

  const errors = part.errorCount ?? 0;
  const warnings = part.warningCount ?? 0;
  // The row list is bounded, so a bigger set must say so rather than look complete.
  const isPartialList = part.shown < part.total;

  const severityBadge = (row: IssueRowData) =>
    row.severity?.toLowerCase() === 'warning' ? (
      <Badge color='warning' size='sm'>
        {t('issueCollection.warning')}
      </Badge>
    ) : (
      <Badge color='error' size='sm'>
        {t('issueCollection.error')}
      </Badge>
    );

  return (
    <ResultCard>
      <ResultCardHeader
        title={
          part.invoiceNumber
            ? t('issueCollection.titleFor', { number: part.invoiceNumber })
            : t('issueCollection.title')
        }
      >
        <span className='flex items-center gap-xs'>
          {errors > 0 && (
            <Badge color='error' size='sm'>
              {t('invoiceCard.errorCount', { count: errors })}
            </Badge>
          )}
          {warnings > 0 && (
            <Badge color='warning' size='sm'>
              {t('invoiceCard.warningCount', { count: warnings })}
            </Badge>
          )}
        </span>
      </ResultCardHeader>

      <CardActions>
        <CardButton onClick={() => setIsOpen(true)}>
          {t('issueCollection.showAll', { count: part.shown })}
        </CardButton>
      </CardActions>

      <Modal open={isOpen} onClose={() => setIsOpen(false)} size='lg'>
        <Modal.Header
          onClose={() => setIsOpen(false)}
          label={
            part.invoiceNumber
              ? t('issueCollection.titleFor', { number: part.invoiceNumber })
              : t('issueCollection.title')
          }
          description={
            isPartialList
              ? t('issueCollection.partialNotice', { shown: part.shown, total: part.total })
              : undefined
          }
        />
        <Modal.Content>
          <SimpleTable
            headers={[
              t('issueCollection.severity'),
              t('issueCollection.field'),
              t('issueCollection.problem')
            ]}
            data={part.issues}
            scrollable
            renderRow={row => [
              <span key='s'>{severityBadge(row)}</span>,
              <span key='f' className='text-foreground-muted'>
                {labelForPath(row.path ?? undefined) ?? '—'}
              </span>,
              <span key='m'>{row.message}</span>
            ]}
          />
        </Modal.Content>
      </Modal>
    </ResultCard>
  );
}
